<script>
import { getInboxIconColor } from 'dashboard/helper/inbox';

export default {
  props: {
    // Color de marca calculado por la bandeja (distingue QR de API, que
    // comparten ícono). Si no viene, se deduce del ícono.
    iconColorOverride: {
      type: String,
      default: '',
    },
    // Bandeja que un admin puede ocultar del menú con clic derecho.
    hideable: {
      type: Boolean,
      default: false,
    },
    to: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: '',
    },
    labelColor: {
      type: String,
      default: '',
    },
    shouldTruncate: {
      type: Boolean,
      default: false,
    },
    icon: {
      type: String,
      default: '',
    },
    warningIcon: {
      type: String,
      default: '',
    },
    showChildCount: {
      type: Boolean,
      default: false,
    },
    childItemCount: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return { contextMenu: null };
  },
  computed: {
    iconColor() {
      return this.iconColorOverride || getInboxIconColor(this.icon);
    },
    showIcon() {
      return {
        'overflow-hidden whitespace-nowrap text-ellipsis': this.shouldTruncate,
      };
    },
    isCountZero() {
      return this.childItemCount === 0;
    },
    // Nombre completo al pasar el mouse, rápido (el title nativo tarda ~1 s).
    menuTooltip() {
      if (!this.shouldTruncate) return null;
      return { content: this.label, delay: { show: 150, hide: 0 } };
    },
  },
  methods: {
    openContextMenu(e) {
      if (!this.hideable) return;
      e.preventDefault();
      this.contextMenu = { x: e.clientX, y: e.clientY };
    },
    hide() {
      this.contextMenu = null;
      this.$emit('hide');
    },
  },
};
</script>

<template>
  <router-link
    v-slot="{ href, isActive, navigate }"
    :to="to"
    custom
    active-class="active"
  >
    <li
      class="font-medium h-7 my-1 transition-all duration-150 hover:text-bg-50 flex items-center px-2 rounded-md"
      :class="{
        'bg-woot-25 dark:bg-slate-800': isActive,
        'hover:bg-woot-50 dark:hover:bg-woot-500/10 hover:ring-1 hover:ring-inset hover:ring-woot-100 dark:hover:ring-woot-500/20': !isActive,
        'text-ellipsis overflow-hidden whitespace-nowrap max-w-full':
          shouldTruncate,
      }"
      @click="navigate"
    >
      <!-- El tooltip va en la fila (ancho visible) y no en el texto, que se
           extiende por debajo del recorte y hacía que el tooltip se
           posicionara mal y parpadeara. -->
      <a
        v-tooltip.right="menuTooltip"
        :href="href"
        class="inline-flex text-left max-w-full w-full items-center"
        @contextmenu="openContextMenu"
      >
        <span
          v-if="icon"
          class="inline-flex items-center justify-center w-4 rounded-sm p-0.5 mr-1.5 rtl:mr-0 rtl:ml-1.5"
          :class="{ 'bg-slate-100 dark:bg-slate-700': !iconColor }"
          :style="iconColor ? { backgroundColor: `${iconColor}26` } : null"
        >
          <fluent-icon
            class="text-xxs"
            :class="{
              'text-slate-700 dark:text-slate-200': !iconColor && !isActive,
              'text-woot-500 dark:text-woot-500': !iconColor && isActive,
            }"
            :style="iconColor ? { color: iconColor } : null"
            :icon="icon"
            size="12"
          />
        </span>

        <span
          v-if="labelColor"
          class="inline-flex rounded-full bg-slate-100 h-3 w-3 mr-1.5 rtl:mr-0 rtl:ml-1.5 border border-slate-50 dark:border-slate-900"
          :style="{ backgroundColor: labelColor }"
        />
        <div
          class="items-center flex overflow-hidden whitespace-nowrap text-ellipsis w-full justify-between"
        >
          <span
            class="text-sm text-slate-700 dark:text-slate-100"
            :class="{
              'text-woot-500 dark:text-woot-500': isActive,
              'text-ellipsis overflow-hidden whitespace-nowrap max-w-full':
                shouldTruncate,
            }"
          >
            {{ label }}
          </span>
          <span
            v-if="showChildCount"
            class="bg-slate-50 dark:bg-slate-700 rounded-full min-w-[18px] justify-center items-center flex text-xxs font-medium mx-1 py-0 px-1"
            :class="
              isCountZero
                ? `text-slate-300 dark:text-slate-500`
                : `text-slate-700 dark:text-slate-50`
            "
          >
            {{ childItemCount }}
          </span>
        </div>
        <span
          v-if="warningIcon"
          class="inline-flex mr-1 bg-red-50 dark:bg-red-900 p-0.5 rounded-sm"
        >
          <fluent-icon
            v-tooltip.top-end="$t('SIDEBAR.REAUTHORIZE')"
            class="text-xxs text-red-500 dark:text-red-300"
            :icon="warningIcon"
            size="12"
          />
        </span>
      </a>
      <woot-context-menu
        v-if="contextMenu"
        :x="contextMenu.x"
        :y="contextMenu.y"
        @close="contextMenu = null"
      >
        <div
          class="p-1 bg-white border border-solid rounded-md shadow-lg dark:bg-slate-800 border-slate-100 dark:border-slate-700"
        >
          <button
            type="button"
            class="flex items-center w-full gap-2 px-2 py-1.5 text-sm rounded text-slate-700 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-700"
            @mousedown.prevent="hide"
          >
            <fluent-icon icon="eye-hide" size="14" />
            {{ $t('SIDEBAR.HIDE_INBOX') }}
          </button>
        </div>
      </woot-context-menu>
    </li>
  </router-link>
</template>
